/**
 * Interactive Lesson points — scoring rules.
 *
 * Point VALUES come from the canonical lesson content the server re-reads, and
 * CORRECTNESS is recomputed server-side from the learner's submitted answer
 * against the canonical answer key (`sectionEarnedPoints` ignores any client
 * `correct` flag). Only `attempts` (the first-try multiplier) is client-asserted,
 * so a learner who answered correctly could at most claim full instead of the
 * retry-reduced amount — they cannot earn points without submitting the right
 * answer. See the "Interactive lesson points (ILE)" section of
 * `docs/lesson-ui-data-contract.md`.
 */

import { isMultiSelectMcq, mcqCorrectIndices, type LessonBlock, type LessonSection } from "@/lib/lesson-content/schema";

/** Policy knobs. Change here only. */
export const LESSON_OPEN_BONUS = 5; // once per lesson, on first open
export const RETRY_FACTOR = 0.5; // graded block, correct on the second attempt; later attempts earn 0
export const EXPLORE_POINTS = 5; // completing an explore / no-right-answer block

/** Hints a learner may take on a hinted block (categorization, sequencing). */
export const MAX_HINTS = 2;
/** Multiplier by hints taken — index 0 is "no hint". Applied after RETRY_FACTOR. */
export const HINT_FACTORS = [1, 0.9, 0.75] as const;

/**
 * Reduce a correct answer's points for hints taken, never below 1.
 *
 * The floor matters: a hinted-but-correct answer has to stay worth more than a
 * wrong one, or taking a hint is strictly worse than guessing and nobody uses
 * the feature the hints were added for.
 */
export function applyHintPenalty(points: number, hintsUsed?: number): number {
  const raw = Number(hintsUsed ?? 0);
  const used = Number.isFinite(raw) ? Math.min(Math.max(Math.floor(raw), 0), MAX_HINTS) : 0;
  if (used === 0 || points <= 0) return points;
  return Math.max(1, Math.floor(points * HINT_FACTORS[used]));
}

/** Graded blocks have a right answer and earn a first-try bonus. */
const GRADED_TYPES = new Set<LessonBlock["type"]>([
  "mcq",
  "categorization",
  "sequencing",
  "fill_blank",
  "color_sort",
]);

/** Explore blocks are completion-only — flat points, no first-try concept. */
const EXPLORE_TYPES = new Set<LessonBlock["type"]>([
  "image_hotspot",
  "flip_cards",
  "accordion_tabs",
  "stepped_timeline",
  "wheel_diagram",
  "nested_layers",
  "format_switcher",
  "image_switcher",
  "vertical_roadmap",
  "add_next_layer",
  "context_window_slider",
  "cliche_highlighter",
  "tokenizer_playground",
  "be_the_model",
  "workflow_simulator",
  "agent_simulator",
  "dual_chatbot",
  "data_sandbox",
  "checklist",
  "free_response",
  "quick_question",
  "tile_reveal",
  "slider_set",
  "scenario_sort",
  "spot_the_errors",
  "decision_tree",
  "prompt_builder",
  "filter_compare",
  "train_model",
  "meaning_map",
]);

/**
 * Tabs are optional reading: they never gate a part, so they carry no points either.
 * A part is scored once, when its REQUIRED blocks are done, so points on an optional
 * block would depend on whether the learner happened to open it first.
 */
function isUnscoredTabs(block: LessonBlock): boolean {
  return block.type === "accordion_tabs" && block.data.display === "tabs";
}

/** What the client reports for a single completed block. */
export type BlockResult = {
  blockId: string;
  /** Graded blocks only, client-asserted; IGNORED for scoring (server recomputes from `answer`). */
  correct?: boolean;
  /** Attempts taken; 1 = first try. Client-asserted (first-try multiplier only). */
  attempts?: number;
  /** Hints taken on a hinted block. Client-asserted, and can only REDUCE points. */
  hintsUsed?: number;
  /** The learner's submitted answer, validated server-side. Shape per block type:
   *  mcq {selectedIndex}, sequencing {order}, categorization {placements}, fill_blank {selections},
   *  color_sort {picks}. */
  answer?: unknown;
};

/** Max points a block can yield (its full, first-try value). */
export function blockMaxPoints(block: LessonBlock): number {
  switch (block.type) {
    case "mcq":
      return block.data.points;
    case "sequencing":
      return block.data.points;
    case "categorization":
      return block.data.points_per_match * block.data.items.length;
    case "fill_blank":
      return block.data.points_per_blank * block.data.blanks.length;
    case "color_sort":
      return block.data.points_per_item * block.data.items.length;
    case "accordion_tabs":
      return isUnscoredTabs(block) ? 0 : EXPLORE_POINTS;
    case "image_hotspot":
      // No hotspots: just an image, nothing to complete.
      return block.data.hotspots.length === 0 ? 0 : EXPLORE_POINTS;
    case "flip_cards":
    case "stepped_timeline":
    case "wheel_diagram":
    case "nested_layers":
    case "format_switcher":
    case "image_switcher":
    case "add_next_layer":
    case "context_window_slider":
    case "cliche_highlighter":
    case "tokenizer_playground":
    case "be_the_model":
    case "workflow_simulator":
    case "agent_simulator":
    case "dual_chatbot":
    case "data_sandbox":
    case "checklist":
    case "free_response":
    case "train_model":
    case "quick_question":
    case "slider_set":
    case "scenario_sort":
    case "spot_the_errors":
    case "decision_tree":
    case "prompt_builder":
      return EXPLORE_POINTS;
    case "vertical_roadmap":
    case "filter_compare":
    case "meaning_map":
    case "tile_reveal":
      return 0; // optional, like tabs: never gates, so never scores
    case "rich_text":
    case "image":
    case "workplace_scenario":
    case "card_grid":
    case "compare_columns":
    case "project_export":
      return 0;
  }
}

/**
 * Points for a graded block answered correctly on attempt `attempts`: full on the first try,
 * `RETRY_FACTOR` of it on the second, nothing after that. Only one retry is credited because
 * a learner who clicks through every wrong option of a 4-option MCQ would otherwise be
 * guaranteed half marks by elimination.
 */
function gradedPoints(max: number, attempts: number | undefined): number {
  const n = attempts ?? 1;
  if (n <= 1) return max;
  if (n === 2) return Math.floor(max * RETRY_FACTOR);
  return 0;
}

/**
 * CLIENT-DISPLAY ONLY: points to show in-block, trusting the caller's `correct`.
 * The server does NOT use this for crediting (see `sectionEarnedPoints`).
 */
export function blockEarnedPoints(block: LessonBlock, result: BlockResult): number {
  if (EXPLORE_TYPES.has(block.type)) {
    return blockMaxPoints(block); // completion is the only requirement
  }
  if (block.type === "color_sort") return colorSortPoints(block, result.answer);
  if (GRADED_TYPES.has(block.type)) {
    if (!result.correct) return 0;
    const base = gradedPoints(blockMaxPoints(block), result.attempts);
    return applyHintPenalty(base, result.hintsUsed);
  }
  return 0; // content blocks
}

/**
 * A colour sort is answered once and credited per item: `points_per_item` for each item put in
 * its right category. It used to be all-or-nothing, with "Try again" on the wrong ones until
 * everything was right, which made learners re-enter answers just to be allowed on.
 */
export function colorSortPoints(block: Extract<LessonBlock, { type: "color_sort" }>, answer: unknown): number {
  const picks = (answer as { picks?: Record<string, unknown> })?.picks;
  if (!picks || typeof picks !== "object") return 0;
  return block.data.items.filter((item) => picks[item.id] === item.category_id).length * block.data.points_per_item;
}

/** Did the learner's submitted answer match the block's canonical answer key? */
export function isBlockAnswerCorrect(block: LessonBlock, answer: unknown): boolean {
  switch (block.type) {
    case "mcq": {
      const correct = mcqCorrectIndices(block.data);
      if (isMultiSelectMcq(block.data)) {
        const picked = (answer as { selectedIndices?: unknown })?.selectedIndices;
        return (
          Array.isArray(picked) &&
          picked.length === correct.length &&
          new Set(picked).size === picked.length &&
          picked.every((i) => correct.includes(i as number))
        );
      }
      const a = answer as { selectedIndex?: number } | undefined;
      return a?.selectedIndex === correct[0];
    }
    case "sequencing": {
      const order = (answer as { order?: unknown })?.order;
      return (
        Array.isArray(order) &&
        order.length === block.data.correct_order.length &&
        order.every((id, i) => id === block.data.correct_order[i])
      );
    }
    case "categorization": {
      const placements = (answer as { placements?: Record<string, unknown> })?.placements;
      if (!placements || typeof placements !== "object") return false;
      return block.data.items.every((item) => placements[item.id] === item.correct_bucket_id);
    }
    case "fill_blank": {
      const selections = (answer as { selections?: Record<string, unknown> })?.selections;
      if (!selections || typeof selections !== "object") return false;
      return block.data.blanks.every((b) => selections[b.id] === b.correct_index);
    }
    case "color_sort": {
      const picks = (answer as { picks?: Record<string, unknown> })?.picks;
      if (!picks || typeof picks !== "object") return false;
      return block.data.items.every((item) => picks[item.id] === item.category_id);
    }
    default:
      return false; // non-graded blocks have no answer key
  }
}

/**
 * SERVER-AUTHORITATIVE points for a completed section. Correctness is recomputed
 * from each block's submitted `answer` against the canonical content (the client
 * `correct` flag is ignored). Only blocks belonging to the section are credited;
 * a graded block with a wrong/missing answer earns 0.
 */
export function sectionEarnedPoints(
  section: LessonSection,
  results: BlockResult[],
): number {
  const byId = new Map(results.map((r) => [r.blockId, r]));
  let total = 0;
  for (const block of section.blocks) {
    const result = byId.get(block.id);
    if (!result) continue;
    if (EXPLORE_TYPES.has(block.type)) {
      total += blockMaxPoints(block);
      continue;
    }
    if (block.type === "color_sort") {
      total += colorSortPoints(block, result.answer);
      continue;
    }
    if (GRADED_TYPES.has(block.type)) {
      if (!isBlockAnswerCorrect(block, result.answer)) continue;
      const base = gradedPoints(blockMaxPoints(block), result.attempts);
      total += applyHintPenalty(base, result.hintsUsed);
    }
  }
  return total;
}
